import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '@/state/session';
import { HelpButton, HelpModal } from './HelpModal';

/**
 * Page-level floating help button. Lives outside any nav row so it never
 * competes for horizontal space with the marquee. Renders on every route
 * (including /login) — the modal copy switches based on a UNION of auth
 * state and route:
 *   - signed out anywhere → login copy (a signed-out visitor on `/` still
 *     needs login help, not gameplay help)
 *   - signed in but still on /login → login copy (the admin-password step
 *     is post-verify but pre-flow-complete; the session is set but the
 *     user hasn't left the login surface yet)
 *   - signed in on any other route → gameplay copy
 *
 * Position: fixed bottom-right with safe-area insets. Bottom-right is the
 * conventional spot for help/chat widgets (Intercom, Crisp) and stays
 * clear of the top nav's identity dropdown chevron, which the old
 * top-right placement was visually swallowing on narrow viewports. z-40
 * keeps it above page content but under the modal (z-[100]).
 *
 * iOS Safari's collapsing bottom toolbar lives outside the layout
 * viewport, so the icon moves naturally with the rest of the page when
 * the toolbar slides in/out — no extra handling needed.
 */
export function FloatingHelp() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Auth alone isn't enough — the admin-password step in the login flow
  // sets the session cookie + Zustand user BEFORE the user has finished
  // signing in, so the route check rules out the gameplay copy on any
  // step that's still inside /login.
  const user = useSession((s) => s.user);

  const showLoginCopy = !user || location.pathname === '/login';
  const close = () => setOpen(false);

  return (
    <>
      <div
        // z-50 keeps the icon over the page (sticky nav is also z-40,
        // later in DOM). Still under the modal at z-[100]. Bottom-right
        // with safe-area insets means the icon stays clear of iPhone
        // home-indicator and rounded corners.
        className="fixed z-50"
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
          right: 'calc(env(safe-area-inset-right, 0px) + 1rem)',
        }}
      >
        <HelpButton
          onClick={() => setOpen(true)}
          label={showLoginCopy ? 'Login help' : 'How tournaments work'}
        />
      </div>

      {open && (
        <HelpModal
          title={showLoginCopy ? 'Login help' : 'How tournaments work'}
          onClose={close}
        >
          {showLoginCopy ? (
            <LoginHelpCopy />
          ) : (
            <NavHelpCopy
              onLeaderboard={() => {
                close();
                navigate('/tournament/active/leaderboard');
              }}
            />
          )}
        </HelpModal>
      )}
    </>
  );
}

function LoginHelpCopy() {
  return (
    <>
      <section className="space-y-2">
        <h3 className="font-semibold text-cabinet-cream">Logging in</h3>
        <ol className="list-decimal list-inside space-y-1 text-cabinet-cream/80">
          <li>Enter your email address</li>
          <li>Check your inbox for a 6-digit code (expires in 10 minutes)</li>
          <li>Enter the code to sign in</li>
        </ol>
      </section>
      <section className="space-y-1.5">
        <h3 className="font-semibold text-cabinet-cream">First time here?</h3>
        <p className="text-cabinet-cream/80">
          After your first sign-in, you'll choose a display name. New accounts
          need to be approved by the host before you can join a tournament.
          You'll see your status on your profile page.
        </p>
      </section>
      <section className="space-y-1.5">
        <h3 className="font-semibold text-cabinet-cream">
          Trouble getting the code?
        </h3>
        <p className="text-cabinet-cream/80">
          Check your spam folder. If it's still missing after a few minutes,
          ask the host to check the system.
        </p>
      </section>
    </>
  );
}

function NavHelpCopy({ onLeaderboard }: { onLeaderboard: () => void }) {
  return (
    <>
      <section className="space-y-2">
        <h3 className="font-semibold text-cabinet-cream">How tournaments work</h3>
        <ol className="list-decimal list-outside ml-5 space-y-1.5 text-cabinet-cream/80">
          <li>Sign up for the active tournament from the home screen</li>
          <li>
            Pay your entry fee (cash, Venmo, or PayPal — see the join screen
            for details)
          </li>
          <li>Wait until the host verifies your entry</li>
          <li>
            Once at the machine: open the score submit screen, take a photo of
            the current credit balance, and enter the balance in the app (this
            is your starting balance — the photo must be taken live from the
            app, you can't upload from your camera roll)
          </li>
          <li>Play your pulls</li>
          <li>
            From the same screen: take a photo of the ending balance and enter
            the ending balance in the app
          </li>
          <li>Submit your score, then wait until the host verifies it</li>
        </ol>
      </section>
      <section className="space-y-1.5">
        <h3 className="font-semibold text-cabinet-cream">Following along</h3>
        <p className="text-cabinet-cream/80">
          Check{' '}
          <button
            type="button"
            onClick={onLeaderboard}
            className="text-cabinet-gold underline underline-offset-2 hover:text-cabinet-gold-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-cabinet-gold rounded"
          >
            the leaderboard
          </button>
          {' '}anytime to see live standings.
        </p>
      </section>
      <section className="space-y-1.5">
        <h3 className="font-semibold text-cabinet-cream">Winning</h3>
        <p className="text-cabinet-cream/80">
          Your score is{' '}
          <code className="text-cabinet-gold-bright tabular-nums">
            ending balance − starting balance
          </code>
          . Highest score wins. The top finishers split the pot. If there's a
          tie for a paying place, tied players take a tiebreaker round.
        </p>
      </section>
    </>
  );
}
